/**
 * A project's SharePoint folder (`plans/sharepoint-sync.md`): its link, how
 * its sync stands, and "Sync now". The files themselves arrive through the
 * import, and are listed with the project's other documents.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SharepointFolderPreview, SharepointStatus } from "@gb/contracts";
import { api, errorMessage } from "@/api/client";
import { documentKeys, importKeys } from "./documents-api";
import { runMicrosoftPopup } from "./microsoft-popup";

export const sharepointKeys = {
  status: (projectId: string) => ["sharepoint", projectId] as const,
};

/**
 * How often the status is read again. The worker polls the folder once a
 * minute, so every 30 seconds shows each poll within half a minute of it,
 * and quicker while files are waiting, so they are seen arriving.
 */
const READ_EVERY_MS = 30_000;
const READ_WHILE_WAITING_MS = 5_000;
const READ_WHILE_CHECKING_MS = 2_000;

export function useSharepointStatus(projectId: string) {
  return useQuery({
    queryKey: sharepointKeys.status(projectId),
    queryFn: async (): Promise<SharepointStatus> => {
      const res = await api.sharepoint.status({ params: { id: projectId } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    refetchInterval: (query) => {
      const link = query.state.data?.link;
      if (!link) return false;
      // A poll is on its way: its answer is shown as soon as it is in.
      if (query.state.data?.checking) return READ_WHILE_CHECKING_MS;
      return link.waiting > 0 || link.health === "waiting" ? READ_WHILE_WAITING_MS : READ_EVERY_MS;
    },
  });
}

/**
 * Find the folder at an address and count its files. Links nothing.
 *
 * First the person signs in with Microsoft in a popup, so the API can check,
 * as them, that the folder opens for them in SharePoint (2026-10-19). The
 * preview carries the proof the link needs.
 */
export function usePreviewSharepointFolder(projectId: string) {
  return useMutation({
    mutationFn: async (url: string): Promise<SharepointFolderPreview> => {
      const started = await api.sharepoint.checkStart({ params: { id: projectId }, body: { url } });
      if (started.status !== 200) throw new Error(errorMessage(started));
      const { code, state } = await runMicrosoftPopup(started.body.url);
      const res = await api.sharepoint.preview({ params: { id: projectId }, body: { url, code, state } });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
  });
}

/** What follows a change of link: the status, and the files and imports it brings. */
function useRefresh(projectId: string) {
  const queryClient = useQueryClient();
  return (status?: SharepointStatus) => {
    if (status) queryClient.setQueryData(sharepointKeys.status(projectId), status);
    void queryClient.invalidateQueries({ queryKey: sharepointKeys.status(projectId) });
    void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
    void queryClient.invalidateQueries({ queryKey: importKeys.list(projectId) });
  };
}

/** Link the folder a preview checked, with that preview's proof. */
export function useLinkSharepointFolder(projectId: string) {
  const refresh = useRefresh(projectId);
  return useMutation({
    mutationFn: async (input: { url: string; proof: string }): Promise<SharepointStatus> => {
      const res = await api.sharepoint.link({ params: { id: projectId }, body: input });
      if (res.status !== 200) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: refresh,
  });
}

export function useUnlinkSharepointFolder(projectId: string) {
  const refresh = useRefresh(projectId);
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const res = await api.sharepoint.unlink({ params: { id: projectId } });
      if (res.status !== 204) throw new Error(errorMessage(res));
    },
    onSuccess: () => refresh(),
  });
}

export function useSyncSharepointNow(projectId: string) {
  const refresh = useRefresh(projectId);
  return useMutation({
    mutationFn: async (): Promise<SharepointStatus> => {
      const res = await api.sharepoint.syncNow({ params: { id: projectId }, body: {} });
      if (res.status !== 202) throw new Error(errorMessage(res));
      return res.body;
    },
    onSuccess: refresh,
  });
}
